import { Component, ChangeDetectionStrategy, ChangeDetectorRef, Input, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule, ModalController } from '@ionic/angular';
import { MatTooltipModule } from '@angular/material/tooltip';

import { CameraProvider } from 'src/shared/providers/camera/camera';
import { JobMediasProvider } from 'src/providers/job-medias/job-medias.provider';
import { ToDos } from 'src/providers/to-dos/to-dos';
import { Util } from 'src/shared/util/util';

import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';

import { SharedModule } from 'src/shared/shared.module';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';

@Component({
  selector: 'tidy-ongoing-checklist',
  templateUrl: './ongoing-checklist.component.html',
  standalone: true,
  imports: [CommonModule, IonicModule, MatTooltipModule, SharedModule, TidyUiComponentsModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OngoingChecklistComponent implements OnInit, OnChanges {

  @Input() booking: any;
  @Input() isNativeMobile: boolean;
  @Input() jobId: number;
  @Input() addressId: number;

  rooms: any[] = [];
  beforePhotos: any[] = [];
  afterPhotos: any[] = [];
  beforeAfterState: any;
  didProStartToDos = false;
  isToDosLoaded = false;

  private cameraProvider = inject(CameraProvider);
  private jobMediasProvider = inject(JobMediasProvider);
  private toDosProvider = inject(ToDos);
  private util = inject(Util);
  private modalCtrl = inject(ModalController);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit() {
    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['booking'] && !changes['booking'].firstChange) {
      this.loadData();
    }
  }

  async loadData() {
    this.isToDosLoaded = false;
    this.didProStartToDos = false;
    this.cdr.markForCheck();
    if (!this.booking?.booking?.task_list?.id) {
      this.isToDosLoaded = true;
      this.cdr.markForCheck();
      return;
    }
    await this.loadRooms();
    this.isToDosLoaded = true;
    this.cdr.markForCheck();
  }

  async loadRooms(): Promise<void> {
    this.rooms = await this.toDosProvider.getToDosForPastJob(this.jobId);
    if (this.rooms.length) {
      const toDoLists = await this.toDosProvider.getAllToDoListsForAddress(this.addressId);
      const list = toDoLists.find(todoList => todoList.id === this.booking?.booking?.task_list?.id);
      this.beforeAfterState = this.getBeforeAfterState(list?.before_after_photos_state);
      this.rooms = this.toDosProvider.buildRoomIcons(this.rooms);
      const jobMedias = await this.jobMediasProvider.getJobMedias([this.jobId]);
      const beforeJobMedias = jobMedias.filter(m => m.category === 'before_job');
      const afterJobMedias = jobMedias.filter(m => m.category === 'after_job');
      this.beforePhotos = await this.getParsedJobMediasWithMetadata(beforeJobMedias);
      this.afterPhotos = await this.getParsedJobMediasWithMetadata(afterJobMedias);
      this.didProStartToDos = true;
    } else {
      this.didProStartToDos = false;
    }
    if (this.rooms) {
      this.rooms.forEach(room => {
        if (room.todos) {
          room.todos.forEach(toDo => {
            if (toDo.performance === 'performed') {
              toDo.toDoIcon = 'assets/img/to-do-done.svg';
            } else if (toDo.performance === 'not_performed') {
              toDo.toDoIcon = 'assets/img/to-do-not-done.svg';
            } else {
              toDo.toDoIcon = 'assets/img/to-do-not-marked.svg';
            }

            if (toDo.performance === 'performed') {
              toDo.toDoTooltip = 'Marked as performed by pro';
            } else if (toDo.performance === 'not_performed') {
              toDo.toDoTooltip = 'Marked as not performed by pro';
            } else {
              toDo.toDoTooltip = 'Not marked by pro';
            }
          });
        }
      });
    }
  }

  getBeforeAfterState(state) {
    if (state === 'photo') {
      return {
        before: '(Before Photo Requested)',
        after: '(After Photo Requested)'
      }
    } else if (state === 'before_photo_only') {
      return {
        before: '(Before Photo Requested)',
        after: '(None Requested)'
      }
    } else if (state === 'after_photo_only') {
      return {
        before: '(None Requested)',
        after: '(After Photo Requested)'
      }
    } else if (state === 'video') {
      return {
        before: '(Before Video Requested)',
        after: '(After Video Requested)'
      }
    } else if (state === 'before_video_only') {
      return {
        before: '(Before Video Requested)',
        after: '(None Requested)'
      }
    } else if (state === 'after_video_only') {
      return {
        before: '(None Requested)',
        after: '(After Video Requested)'
      }
    } else {
      return {
        before: '(None Requested)',
        after: '(None Requested)'
      }
    }
  }

  async loadJobMedias(): Promise<any> {
    const jobMedias = await this.jobMediasProvider.getJobMedias([this.jobId]);
    const beforePhotos = jobMedias.filter((jobMedia) => jobMedia.category === 'before_job');
    const afterPhotos = jobMedias.filter((jobMedia) => jobMedia.category === 'after_job');
    return {
      beforePhotos,
      afterPhotos,
    };
  }

  async getParsedJobMediasWithMetadata(jobMedias: any[]) {
    const homekeepers = this.booking?.booking?.current_job?.homekeepers || [];
    jobMedias = await this.toDosProvider.processFilesAndReturn(jobMedias);
    return jobMedias.map((jobMedia) => {
      const homekeeper = homekeepers.find((hk) => hk.id === jobMedia.uploaded_by_id);
      const homekeeperName = homekeeper ? `${homekeeper.first_name || ''} ${homekeeper.last_name || ''}`.trim() : null;
      return {
        ...jobMedia,
        mediaType: jobMedia.media_format,
        url: jobMedia.media_url,
        uploadedBy: jobMedia.uploaded_by_type === 'User' ? homekeeperName : null,
      };
    });
  }

  async takeVideo(categoryType: string) {
    let loading;
    try {
      const parsedJobMedia = await this.jobMediasProvider.takeBeforeAfterVideo(categoryType, this.jobId);
      loading = await this.util.showLoading();
      await this.jobMediasProvider.saveJobMedias(this.jobId, [parsedJobMedia]);
      const { beforePhotos, afterPhotos } = await this.loadJobMedias();
      if (categoryType === 'before_job') {
        this.beforePhotos = await this.getParsedJobMediasWithMetadata(beforePhotos);
      } else {
        this.afterPhotos = await this.getParsedJobMediasWithMetadata(afterPhotos);
      }
      this.cdr.markForCheck();
      loading.dismiss();
    } catch (err) {
      loading.dismiss();
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  async takePhoto(categoryType: string) {
    this.cameraProvider.getImg().then(async (photo) => {
      if (!photo?.base64String && !photo?.dataUrl) {
        return;
      }
      const parsedPhoto = photo?.dataUrl && !photo?.base64String ? photo?.dataUrl : `data:image/jpeg;base64,${photo.base64String}`;
      const newPhoto = { url: parsedPhoto, mediaType: 'photo' };
      const parsedJobMedia = await this.jobMediasProvider.saveBeforeAfterPhoto(newPhoto.url, this.jobId, categoryType);
      await this.jobMediasProvider.saveJobMedias(this.jobId, [parsedJobMedia]);
      const { beforePhotos, afterPhotos } = await this.loadJobMedias();
      if (categoryType === 'before_job') {
        this.beforePhotos = await this.getParsedJobMediasWithMetadata(beforePhotos);
      } else {
        this.afterPhotos = await this.getParsedJobMediasWithMetadata(afterPhotos);
      }
      this.cdr.markForCheck();
    });
  }

  async uploadVideo(videos: any[], categoryType: string) {
    try {
      const parsedJobMedias = [];
      await Promise.all(
        videos.map(async (video) => {
          const parsedJobMedia = await this.jobMediasProvider.uploadAndSaveVideo(
            video.file,
            this.jobId,
            categoryType,
            video.file
          );
          parsedJobMedias.push(parsedJobMedia);
        })
      );
      await this.jobMediasProvider.saveJobMedias(this.jobId, parsedJobMedias);
      const { beforePhotos, afterPhotos } = await this.loadJobMedias();
      if (categoryType === 'before_job') {
        this.beforePhotos = await this.getParsedJobMediasWithMetadata(beforePhotos);
      } else {
        this.afterPhotos = await this.getParsedJobMediasWithMetadata(afterPhotos);
      }
      this.cdr.markForCheck();
    } catch (err) {
      this.util.showError('Error uploading video. Please try again.', err);
    }
  }

  async uploadPhoto(photos: any[], categoryType: string) {
    const loading = await this.util.showLoading();
    const parsedJobMedias = [];
    try {
      await Promise.all(
        photos.map(async (photo) => {
          const parsedJobMedia = await this.jobMediasProvider.saveBeforeAfterPhoto(
            photo.url,
            this.jobId,
            categoryType,
            photo?.file,
            photo?.exif
          );
          parsedJobMedias.push(parsedJobMedia);
        })
      );
      await this.jobMediasProvider.saveJobMedias(this.jobId, parsedJobMedias);
      const { beforePhotos, afterPhotos } = await this.loadJobMedias();
      if (categoryType === 'before_job') {
        this.beforePhotos = await this.getParsedJobMediasWithMetadata(beforePhotos);
      } else {
        this.afterPhotos = await this.getParsedJobMediasWithMetadata(afterPhotos);
      }
      this.cdr.markForCheck();
    } catch (err) {
      this.util.showError('Error uploading photo. Please try again.', err);
    } finally {
      loading.dismiss();
    }
  }

  async removePhoto(image, index, categoryType) {
    const params: ConfirmPageParamsModel = {
      title: image?.mediaType === 'photo' ? 'Remove photo?' : 'Remove video?',
      body: '',
      backText: 'Go Back',
      confirmText: 'Remove ',
      confirmAction: this.confirmRemovePhoto.bind(this, image, index, categoryType)
    }
    const confirmationModal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal'
    });
    await confirmationModal.present();
  }

  async confirmRemovePhoto(image, index, categoryType) {
    const loading = await this.util.showLoading();
    try {
      await this.jobMediasProvider.deleteJobMedia(image.id);
      if (categoryType === 'before_job') {
        this.beforePhotos.splice(index, 1);
      } else {
        this.afterPhotos.splice(index, 1);
      }
      this.cdr.markForCheck();
    } catch (err) {
      this.util.showError('Error removing photo. Please try again.', err);
    } finally {
      loading.dismiss();
    }
    this.modalCtrl.dismiss();
  }
}
